<script lang="ts">
    import Fa from 'svelte-fa'
    import { faEdit, faPlusCircle, faShareAlt, faUser } from '@fortawesome/free-solid-svg-icons'
    import { onMount } from 'svelte/internal'
    import { QuotationEntity } from '../../../modules/posApi/quotation/quotation.entity'
    import type { SharePageEntity } from '../../../modules/toolsApi/share-pages/share-page.entity'
    import SharePageLink from '../../_common/share-page-link.svelte'
    import dayjs from 'dayjs'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Content from '../../_common/content.svelte'
    import Popover from '../../_common/popover.svelte'
    import QuotationForm from './quotation-form.svelte'
    import DateSelect from '../../_common/date-select.svelte'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import PosForm from '../pos-form.svelte'
    import Zact from '../../_common/z/zact.svelte'

    let show = false
    let entities: QuotationEntity[] = []
    let total = 0

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()

    onMount(async () => {
        await loadEntities()
    })

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    async function loadEntities() {
        try {
            show = true
            const dt = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                service: true,
            }
            entities = await window.api.pos.quotations.getAll(dt)
            entities = await window.api.utils.utils.assignPersonsFromObject(entities)
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function getBadgeLabel(quotation: QuotationEntity) {
        if (quotation.status === 110) return 'faturado'
        if (quotation.expiresAt) {
            const expiresAt = new Date(quotation.createdAt)
            expiresAt.setDate(expiresAt.getDate() + quotation.expiresAt)
            const diffDays = dayjs(expiresAt).diff(new Date(), 'day')
            if (diffDays < 0) {
                return 'vencido'
            }
        }
        return 'ativo'
    }

    function getBadgeColor(quotation: QuotationEntity) {
        if (quotation.status === 110) return GridBadgeClassEnum.badge_purple // vendeu
        if (quotation.expiresAt) {
            const expiresAt = new Date(quotation.createdAt)
            expiresAt.setDate(expiresAt.getDate() + quotation.expiresAt)
            const diffDays = dayjs(expiresAt).diff(new Date(), 'day')
            if (diffDays < 0) {
                return GridBadgeClassEnum.badge_alert
            }
        }
        return GridBadgeClassEnum.badge_success
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Orçamentos Emitidos'
        grid.subtitle = ''
        grid.fontSize = '15px'
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Nome', type: GridColTypeEnum.string, maxLength: 33 }))
        grid.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnPerson = new GridButton({
                callback: () => window.person('show', item.personId),
                icon: faUser,
                label: '',
            })
            const btnOpen = new GridButton({
                callback: () => editEntity(item.quotationId),
                icon: faEdit,
                label: 'Abrir',
            })
            // compartilhar cria uma página pública do orçamento: exige a mesma permissão do botão
            // que existe dentro do formulário
            const acts: GridButton[] = [btnOpen]
            if (window.store.token.permissions.pos.quotes?.write) {
                acts.push(
                    new GridButton({
                        callback: () => goShare(item.quotationId),
                        icon: faShareAlt,
                        label: '',
                    }),
                )
            }
            acts.push(btnPerson)
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.createdAt })
            line.slots.push({
                content: new GridBadge({
                    label: getBadgeLabel(item),
                    class: getBadgeColor(item),
                }),
            })
            line.slots.push({ content: item.person?.name || '[cliente não informado]' })
            const expiresAt = new Date(item.createdAt)
            expiresAt.setDate(expiresAt.getDate() + item.expiresAt)
            line.slots.push({ content: expiresAt })
            line.slots.push({ content: item.items?.reduce((acc, el) => (acc += el.value * el.quantity), 0) })
            line.slots.push({ content: acts })
            grid.lines.push(line)
        }
    }

    // Mesmo compartilhamento do botão de dentro do orçamento (quotation-form). A lista traz só o
    // resumo de cada orçamento, então o registro completo é buscado aqui — a página compartilhada
    // precisa dos itens com serviço e do cliente, como no formulário.
    let shareToken: string = null
    let sharePersonId: string = null
    async function goShare(id: string) {
        try {
            show = true
            let entity: QuotationEntity = await window.api.pos.quotations.getOne(id)
            if (entity.personId) {
                entity.person = await window.api.events.persons.getOne(entity.personId)
            }
            entity.items = await window.api.utils.utils.assignServicesFromObject(entity.items)
            const dto: Partial<SharePageEntity> = {
                data: [{ type: 'quotation-print', data: { quotation: entity, shared: true } }],
            }
            const result = await window.api.tools.sharePages.create(dto)
            shareToken = result.sharePageId + '_' + result.token
            sharePersonId = entity.personId || entity.person?.personId || null
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    let showForm = false
    let quotationId: string = null
    function editEntity(id: string) {
        quotationId = id
        showForm = true
    }

    function newQuotation() {
        quotationId = null
        showForm = true
    }

    async function formChanged() {
        quotationId = null
        showForm = false
        await loadEntities()
    }

    function formCanceled() {
        quotationId = null
        showForm = false
    }

    let invoiceId = null
    let quotation: QuotationEntity = null
    async function onInvoice(event) {
        if (event.detail.act === 'add') {
            invoiceId = null
            quotation = event.detail.quotation
        } else if (event.detail.act === 'edit') {
            invoiceId = event.detail.quotation.invoiceId
            quotation = null
        } else if (event.detail.act === 'open') {
            await window.api.pos.quotations.setUnpaid(event.detail.quotation.quotationId)
            await loadEntities()
        } else if (event.detail.act === 'closed') {
            await window.api.pos.quotations.setPaid(event.detail.quotation.quotationId, {
                invoiceId: '*',
            })
            await loadEntities()
        }
        quotationId = null
        showForm = false
    }

    function onInvoiceCanceled() {
        quotation = null
        invoiceId = null
    }

    async function onInvoiceChanged(event) {
        if (quotation?.quotationId && event.detail.invoiceId) {
            await window.api.pos.quotations.setPaid(quotation.quotationId, {
                invoiceId: event.detail.invoiceId,
            })
            loadEntities()
        }
        quotation = null
        invoiceId = null
    }
    let print = true
</script>

{#if quotation || invoiceId}
    <Popover
        title="Venda"
        width="1300px"
        height="90vh"
        on:close={() => {
            quotation = null
            invoiceId = null
        }}
    >
        <PosForm {quotation} {invoiceId} on:change={onInvoiceChanged} on:cancel={onInvoiceCanceled} />
    </Popover>
{/if}
{#if shareToken}
    <Popover on:close={() => (shareToken = null)} width="780px" title="Link de compartilhamento">
        <SharePageLink token={shareToken} personId={sharePersonId} />
    </Popover>
{/if}
{#if showForm}
    <Popover
        title="Orçamento"
        width="1300px"
        height="90vh"
        on:close={() => {
            showForm = false
        }}
    >
        <QuotationForm {quotationId} on:change={formChanged} on:cancel={formCanceled} on:invoice={onInvoice} />
    </Popover>
{/if}

<Content footer={true}>
    <Zact>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer h-mobile" />
        {#if window.store.token.permissions.pos.quotes?.write}
            <button class="btn btn-primary" on:click={newQuotation}><Fa icon={faPlusCircle} /> <span class="h-mobile"> Novo orçamento</span></button>
        {/if}
    </Zact>
    <Zgrid {grid} class="h-space" />
    <LoadSpinner {show} />
</Content>

<style>
</style>
